import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Lightbulb, Sparkles, RefreshCw } from "lucide-react";

const KIND_META = {
  celebration: { emoji: "🎉", cls: "bg-emerald-50 border-emerald-200 text-emerald-900" },
  pattern: { emoji: "🔎", cls: "bg-blue-50 border-blue-200 text-blue-900" },
  suggestion: { emoji: "💡", cls: "bg-amber-50 border-amber-200 text-amber-900" },
};

export default function CopilotWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/copilot/insights"); setData(data); } catch { /* silent */ } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const insights = data?.insights || [];

  return (
    <div className="card-soft p-6 relative overflow-hidden" data-testid="copilot-widget">
      <div className="absolute -right-6 -top-6 w-32 h-32 rounded-full bg-gradient-to-br from-blue-100 to-emerald-100 blur-2xl" />
      <div className="relative">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-2xl bg-[#007AFF] text-white flex items-center justify-center"><Sparkles className="w-4 h-4" /></div>
            <div>
              <div className="font-display font-bold text-lg leading-none">Copilot</div>
              <div className="text-[11px] text-gray-500">Your coaching second brain</div>
            </div>
          </div>
          <button data-testid="copilot-refresh" onClick={load} disabled={loading} className="text-gray-400 hover:text-[#007AFF] p-1.5 rounded-lg hover:bg-blue-50 disabled:opacity-40"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /></button>
        </div>

        {loading && !insights.length ? (
          <div className="mt-4 space-y-2">
            <div className="h-3 bg-gray-100 rounded animate-pulse w-4/5" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-3/4" />
            <div className="h-3 bg-gray-100 rounded animate-pulse w-2/3" />
          </div>
        ) : insights.length === 0 ? (
          <div className="mt-4 text-sm text-gray-500 flex items-start gap-2"><Lightbulb className="w-4 h-4 mt-0.5" /> Your Copilot will notice patterns as you coach. Record a game or reflection to unlock insights.</div>
        ) : (
          <ul className="mt-4 space-y-2.5" data-testid="copilot-insights">
            {(expanded ? insights : insights.slice(0, 2)).map((it, i) => {
              const meta = KIND_META[it.kind] || KIND_META.pattern;
              return (
                <li key={i} className={`rounded-2xl border p-3 ${meta.cls}`}>
                  <div className="text-sm leading-relaxed flex gap-2"><span>{meta.emoji}</span><span className="flex-1">{it.text}</span></div>
                  {it.action && <div className="mt-1.5 text-xs font-semibold opacity-80">→ {it.action}</div>}
                </li>
              );
            })}
          </ul>
        )}

        {insights.length > 2 && (
          <button data-testid="copilot-toggle" onClick={()=>setExpanded(!expanded)} className="mt-3 text-xs font-semibold text-[#007AFF] hover:underline">
            {expanded ? "Show less" : `Show all ${insights.length} insights`}
          </button>
        )}
      </div>
    </div>
  );
}
